import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Seo from '../components/Seo';
import { IMAGES, ProtectedImage } from '../lib/images';
import ProtectedBookGallery from '../components/ProtectedBookGallery';
import axios from 'axios';
import {
  Building2,
  Users,
  Search,
  CheckCircle2,
  Briefcase,
  Star,
  Shield,
  Clock,
  ArrowRight,
  UserCheck,
  FileText,
  MessageSquare,
  Zap,
  Sparkles,
  ExternalLink,
  Crown,
  Eye,
  TrendingUp,
  Quote,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AMAZON_BOOK_LINK = "https://a.co/d/0f4Uagyp";

// Track Amazon book click
const trackBookClick = (referrerPage) => {
  try {
    axios.post(`${API_URL}/api/analytics/book-click`, {
      referrer_page: referrerPage
    });
  } catch (error) {
    console.error('Book click tracking failed:', error);
  }
};

// Employer testimonials
const employerTestimonials = [
  {
    name: "Memorial Healthcare System",
    location: "Houston, Texas",
    text: "We've filled 12 positions this year using INeedASocialWorker.com. The flat-rate model means no surprises.",
    rating: 5
  },
  {
    name: "Sunshine Senior Living",
    location: "Phoenix, Arizona", 
    text: "Finally, a site dedicated to social workers. Our applicant quality has never been better.",
    rating: 5
  },
  {
    name: "Great Lakes Mental Health",
    location: "Detroit, Michigan",
    text: "The lowest membership prices we've found anywhere. Worth every penny for access to licensed professionals.",
    rating: 5
  }
];

const TEAM_HERO_IMAGE = "https://customer-assets.emergentagent.com/job_9b86f885-0f5e-479a-98ce-1634cbd87cc9/artifacts/bihsk96g_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2002_17_14%20PM.png";

// Employer-intent hiring FAQ — targets recruiting/job-posting searches
const HIRING_FAQS = [
  { q: 'Where can I post a social work job?', a: 'You can post social work jobs directly on I Need A Social Worker, a specialized nationwide platform built exclusively for the social work profession. Your posting reaches BSW, MSW, LBSW, LMSW and LCSW professionals who are actively looking for social work roles — not a general audience.' },
  { q: 'How do I hire a licensed social worker?', a: 'Create a free employer account, post your position, and reach licensed social workers nationwide. With an employer membership you can search candidate profiles, filter by license type (LBSW, LMSW, LCSW), and message qualified professionals directly.' },
  { q: 'How do I recruit LCSWs, LMSWs, MSWs or BSWs?', a: 'I Need A Social Worker lets you recruit by exactly the credential you need. Post a role or search candidates and filter by LCSW, LMSW, LBSW or MSW, by specialty, and by location — so you reach licensed social workers who match your requirements.' },
  { q: 'Where should I advertise a social work position?', a: 'The best place to advertise a social work position is where social workers already search. As a dedicated social work job board and recruiting platform, I Need A Social Worker puts your opening in front of a focused, relevant audience instead of the general job-seeker pool.' },
  { q: 'Where can I post remote social work jobs?', a: 'Yes — you can post remote, hybrid, part-time, PRN and contract social work jobs. Remote roles are highlighted to candidates specifically searching for remote social work opportunities nationwide.' },
  { q: 'How can hospitals, hospices and home health agencies recruit social workers?', a: 'Healthcare employers use I Need A Social Worker to recruit medical, hospital, hospice, home health and case-management social workers. Post your role and reach licensed professionals experienced in healthcare and end-of-life care settings.' },
  { q: 'How can behavioral health facilities and schools recruit social workers?', a: 'Behavioral health, mental health, psychiatric facilities and school districts can post positions and recruit social workers with the right licensure and specialty experience — including clinical (LCSW) and school social work backgrounds.' },
  { q: 'What makes I Need A Social Worker different from Indeed or ZipRecruiter?', a: 'General job boards reach everyone; I Need A Social Worker reaches social workers. Because the entire audience is social work professionals, employers spend less time filtering unrelated resumes and more time connecting with qualified, licensed candidates.' },
];

const hiringFaqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: HIRING_FAQS.map((f) => ({
    '@type': 'Question', name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const ForEmployers = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const benefits = [
    {
      icon: Users,
      title: "Access Qualified Candidates",
      description: "Browse profiles of licensed LBSWs, LMSWs, and LCSWs ready to work"
    },
    {
      icon: FileText,
      title: "Post Jobs for Free",
      description: "Create job listings at no cost and reach thousands of social workers"
    },
    {
      icon: Search,
      title: "Advanced Search & Filters",
      description: "Filter by license type, experience, location, and availability"
    },
    {
      icon: MessageSquare,
      title: "Direct Messaging",
      description: "Connect directly with candidates through our secure messaging system"
    },
    {
      icon: Shield,
      title: "Verified Professionals",
      description: "All candidates are licensed social work professionals"
    },
    {
      icon: Clock,
      title: "Quick Hiring",
      description: "Reduce time-to-hire with our streamlined application process"
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Create Your Account",
      description: "Sign up as an employer - it's free and takes less than 2 minutes"
    },
    {
      number: "2", 
      title: "Post Your Job",
      description: "Create detailed job listings with requirements, salary, and benefits"
    },
    {
      number: "3",
      title: "Review Applicants",
      description: "Browse applications and connect with qualified candidates"
    },
    {
      number: "4",
      title: "Hire the Best",
      description: "Message candidates directly and make your hire"
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Seo
        exactTitle="Hire Social Workers Nationwide | Post Social Work Jobs & Recruit LCSWs | INASW"
        description="Hire qualified social workers nationwide. Post a social work job, search candidates and recruit licensed LBSWs, LMSWs, LCSWs and MSWs on the specialized social work recruiting platform — not a general job board."
        path="/for-employers"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(hiringFaqJsonLd) }} />
      {/* Hero Section with Team Image Background */}
      <section
        className="relative min-h-[50vh] flex items-center bg-cover bg-center"
        style={{ backgroundImage: `url(${TEAM_HERO_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/85 to-primary-hover/90" />
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="max-w-3xl">
            <Badge className="bg-secondary text-white mb-4">
              <Building2 className="w-3 h-3 mr-1" /> For Employers
            </Badge>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
              Hire Qualified Social Workers Nationwide
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              I Need A Social Worker is a specialized nationwide social work recruiting platform connecting employers with LBSWs, LMSWs, LCSWs and MSWs. Post a job, search candidates, and recruit the licensed professionals you actually need.
            </p>
            
            {/* Why We're Different - Hero Box */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-8 border border-white/20">
              <h3 className="text-secondary font-bold mb-2">Recruit social workers where social workers are already looking</h3>
              <p className="text-white font-bold">
                Stop searching through thousands of unrelated resumes — flat-rate employer access, built only for social work.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate('/register?type=employer')}
                className="bg-secondary hover:bg-secondary-hover text-white h-14 px-8 text-lg"
                data-testid="employer-get-started-btn"
              >
                <Zap className="w-5 h-5 mr-2" />
                Post a Job Free
              </Button>
              <Button 
                onClick={() => {
                  if (isAuthenticated) {
                    navigate('/employer/subscription');
                  } else {
                    navigate('/login?redirect=/employer/subscription');
                  }
                }}
                className="bg-white/20 hover:bg-white/30 text-white border-white/30 h-14 px-8 text-lg"
              >
                {isAuthenticated ? 'View Membership Plans' : 'Login to View Plans'}
              </Button>
            </div>
            {!isAuthenticated && (
              <p className="text-white/70 text-sm mt-3">
                You must be logged in to view membership plans and pricing
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Skip Generic Job Boards Section */}
      <section className="bg-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left - Images */}
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_ba288840-867e-4483-82f4-5d28fd248d68/artifacts/cdct7r7o_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2005_20_29%20PM.png"
                alt="We have the professional licensed social workers you need"
                className="rounded-xl shadow-lg w-full h-auto object-contain"
              />
              <img
                src="https://customer-assets.emergentagent.com/job_ba288840-867e-4483-82f4-5d28fd248d68/artifacts/80hwmr7v_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2005_20_45%20PM%20%281%29.png"
                alt="Skip generic job boards"
                className="rounded-xl shadow-lg w-full h-auto object-contain"
              />
            </div>
            
            {/* Right - Messaging */}
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Skip Generic Job Boards
              </h2>
              <p className="text-lg text-gray-700 mb-4">
                No more sifting through a million careers. <strong>We focus exclusively on licensed social work professionals.</strong>
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Only LBSW, LMSW, and LCSW candidates</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">No unqualified applicants to filter through</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">Every candidate is a licensed professional</span>
                </li>
              </ul>
              <Button 
                onClick={() => navigate('/register?type=employer')}
                className="bg-secondary hover:bg-secondary-hover text-white"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Benefits */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* License Verification Banner */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-12">
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Shield className="w-10 h-10 text-emerald-600" />
              <div className="text-center sm:text-left">
                <h3 className="font-bold text-emerald-800 text-lg">We Verify Professional Licenses</h3>
                <p className="text-emerald-700">All candidate licenses are verified through official state licensing boards</p>
              </div>
              <Badge className="bg-emerald-600 text-white">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Verified Candidates
              </Badge>
            </div>
          </div>
          
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4">
              Why Employers Choose Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The #1 platform for hiring licensed social workers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:bg-gray-100 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-lg text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4">
            How It Works
          </h2>
          <p className="text-lg text-gray-600">
            Start hiring in 4 simple steps
          </p>
        </div>

        <div className="grid md:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="text-center relative">
              <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                {step.number}
              </div>
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gray-200"></div>
              )}
              <h3 className="font-semibold text-lg text-gray-900 mb-2">{step.title}</h3>
              <p className="text-gray-600 text-sm">{step.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Membership Preview */}
      <div className="bg-gradient-to-br from-primary/5 to-secondary/5 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-primary/10 text-primary mb-4">
                <Crown className="w-3 h-3 mr-1" /> Premium Access
              </Badge>
              <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4">
                Unlock Full Access with a Membership
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Post jobs for free, but upgrade to view applicant details, contact candidates directly, and access premium features.
              </p>
              
              {/* Why We're Different - Simplified */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-emerald-800 mb-2">Why We're Different</h4>
                <p className="font-bold text-emerald-700">
                  We have the lowest membership prices in the industry!
                </p>
              </div>
              
              <ul className="space-y-3 mb-8">
                {[
                  'View full applicant profiles',
                  'Direct messaging with candidates',
                  'Browse candidate database',
                  'Priority support',
                  'Unlimited job postings',
                ].map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => {
                    if (isAuthenticated) {
                      navigate('/employer/subscription');
                    } else {
                      navigate('/login?redirect=/employer/subscription');
                    }
                  }}
                  className="bg-primary hover:bg-primary-hover text-white h-12 px-8"
                >
                  View Membership Plans <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              {!isAuthenticated && (
                <p className="text-sm text-gray-500 mt-3">
                  You must be logged in to view membership plans
                </p>
              )}
            </div>
            <div className="text-center">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <Crown className="w-16 h-16 text-secondary mx-auto mb-4" />
                <h3 className="font-serif text-2xl font-bold text-gray-900 mb-2">
                  Premium Memberships
                </h3>
                <p className="text-gray-600 mb-6">
                  Choose from Monthly, 6-Month, or Annual plans. 
                  All with flat-rate pricing and no hidden fees.
                </p>
                <Button 
                  onClick={() => {
                    if (isAuthenticated) {
                      navigate('/employer/subscription');
                    } else {
                      navigate('/login?redirect=/employer/subscription');
                    }
                  }}
                  className="bg-secondary hover:bg-secondary-hover text-white"
                >
                  {isAuthenticated ? 'View Plans & Pricing' : 'Login to View Prices'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Employer Testimonials */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-4">
              Trusted by Employers Nationwide
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {employerTestimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-secondary text-secondary" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-primary/20 mb-3" />
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Book Promotion for Employers */}
      <div className="bg-gradient-to-r from-secondary/10 to-primary/10 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <Badge className="bg-secondary/10 text-secondary w-fit mb-4">
                  For Your Team
                </Badge>
                <h2 className="font-serif text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                  Home Health & Hospice Travel Logbook
                </h2>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Equip your social work staff with the essential organizational tool for field visits. 
                  Perfect for home health, hospice, and traveling social workers.
                </p>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Daily travel logs & mileage tracking
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Patient contact organization
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Expense record keeping
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    Monthly calendar & evaluations
                  </li>
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a 
                    href={AMAZON_BOOK_LINK}
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => trackBookClick('for-employers')}
                    className="inline-flex items-center justify-center gap-2 bg-secondary hover:bg-secondary-hover text-white font-medium px-6 py-3 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Order Copies on Amazon
                  </a>
                </div>
                <p className="text-sm text-gray-500 mt-4">
                  Bulk orders available for agencies and organizations
                </p>
              </div>
              <div
                className="bg-primary/5 p-8 flex items-center justify-center"
                onContextMenu={(e) => e.preventDefault()}
              >
                <div className="w-full max-w-xs">
                  <ProtectedBookGallery />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Employer Hiring FAQ (employer-intent SEO + FAQPage schema) */}
      <div className="bg-white py-16" data-testid="employer-hiring-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-3">Hiring Social Workers: Employer FAQ</h2>
            <p className="text-lg text-gray-600">Where to post, how to recruit, and how to reach licensed social work candidates.</p>
          </div>
          <div className="space-y-4">
            {HIRING_FAQS.map((f, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6" data-testid={`hiring-faq-${i}`}>
                <h3 className="font-semibold text-gray-900 mb-2">{f.q}</h3>
                <p className="text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/employer-faq" className="text-primary font-semibold hover:underline">
              See our License Verification FAQ →
            </Link>
            <span className="hidden sm:inline text-gray-300 mx-3">|</span>
            <Link to="/about" className="text-primary font-semibold hover:underline block sm:inline mt-2 sm:mt-0" data-testid="employer-brand-link">
              About I Need A Social Worker →
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Find Your Next Hire?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Join hundreds of employers already using our platform
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/register?type=employer')}
              className="bg-secondary hover:bg-secondary-hover text-white h-14 px-10 text-lg"
              data-testid="employer-cta-btn"
            >
              <Building2 className="w-5 h-5 mr-2" />
              Get Started - It's Free
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/employer/subscription')}
              className="border-white/30 text-white hover:bg-white/10 h-14 px-10 text-lg"
            >
              View Membership Plans
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForEmployers;
